// ============================================
// P1.5 G-02：组件预览卡片（实时小预览 + 懒渲染 + 图标降级 + 错误兜底）
// ============================================
import { Component, useEffect, useRef, useState, type ReactNode } from 'react';
import type { WidgetDefinition } from '@dtwin/shared';
import CompPreviewBox from './CompPreviewBox';
import { buildPreviewComponent } from './previewComponent';
import { widgetIcon } from '../../registry/widgetIcons';

interface ErrorBoundaryProps {
  fallback: ReactNode;
  children: ReactNode;
}

class PreviewErrorBoundary extends Component<ErrorBoundaryProps, { failed: boolean }> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch() {
    /* 预览失败静默降级 */
  }

  render() {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}

export interface WidgetPreviewCardProps {
  def: WidgetDefinition;
  /** 预览区域尺寸（默认 148x96） */
  width?: number;
  height?: number;
}

/** 图标降级卡片 */
function IconPreview({ def }: { def: WidgetDefinition }) {
  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#7a8ba6',
        fontSize: 30,
      }}
    >
      {widgetIcon(def.type)}
    </div>
  );
}

/** 实时预览：按默认尺寸等比缩放渲染样例组件 */
function LivePreview({ def, width, height }: { def: WidgetDefinition; width: number; height: number }) {
  const size = def.defaultSize || { width: 400, height: 300 };
  const scale = Math.min(width / size.width, height / size.height, 1);
  const comp = buildPreviewComponent(def.type, { width: size.width, height: size.height });
  return (
    <div
      style={{
        width: size.width * scale,
        height: size.height * scale,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          width: size.width,
          height: size.height,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
          position: 'absolute',
          inset: 0,
        }}
      >
        <CompPreviewBox comp={comp} />
      </div>
    </div>
  );
}

export default function WidgetPreviewCard({ def, width = 148, height = 96 }: WidgetPreviewCardProps) {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);
  // P1-3：预览图加载失败回退 live/icon
  const [imgFailed, setImgFailed] = useState(false);

  useEffect(() => {
    setImgFailed(false);
  }, [def.previewImage]);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          io.disconnect();
        }
      },
      { rootMargin: '120px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const kind = def.previewKind || 'live';
  const showImage = !!def.previewImage && !imgFailed;
  return (
    <div
      ref={wrapRef}
      data-testid={`widget-preview-${def.type}`}
      style={{
        width,
        height,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(255,255,255,0.02)',
        borderRadius: 4,
        overflow: 'hidden',
        pointerEvents: 'none',
      }}
    >
      {showImage ? (
        <img
          src={def.previewImage as string}
          alt={def.name}
          data-testid={`widget-preview-img-${def.type}`}
          onError={() => setImgFailed(true)}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : kind === 'icon' ? (
        <IconPreview def={def} />
      ) : visible ? (
        <PreviewErrorBoundary fallback={<IconPreview def={def} />}>
          <LivePreview def={def} width={width} height={height} />
        </PreviewErrorBoundary>
      ) : (
        <div style={{ color: '#3b4a63', fontSize: 12 }}>预览…</div>
      )}
    </div>
  );
}
